import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';

interface Props {
  onBack: () => void;
}

export default function TermsPage({ onBack }: Props) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)', fontFamily: 'Inter, sans-serif' }}>
      {/* Header bar */}
      <div className="sticky top-0 z-50 px-4 py-3 border-b flex items-center gap-3"
        style={{ backgroundColor: 'var(--header-bg)', backdropFilter: 'blur(20px)', borderColor: 'var(--border-color)' }}>
        <button onClick={onBack}
          className="flex items-center gap-2 text-sm font-medium text-[#2563EB] hover:underline transition-colors">
          <ArrowLeft size={16} />
          Volver
        </button>
        <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Términos y Condiciones</span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto px-4 py-10"
      >
        <div className="rounded-2xl p-8 border" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-md)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <FileText size={20} className="text-[#2563EB]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
                Términos y Condiciones
              </h1>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>rastreadordebienes.co · Última actualización: {new Date().toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>

          <div className="prose max-w-none" style={{ color: 'var(--text-primary)' }}>

            <Section title="1. Descargo de Responsabilidad por Inexactitud de la Información">
              <p>
                Nuestra plataforma entregará la información que se encuentre registrada en la base de datos de la
                Superintendencia de Notariado y Registro y el Registro Único Nacional de Tránsito (RUNT). La información
                de estas bases de datos es suministrada por las correspondientes oficinas de instrumentos públicos y los
                organismos de tránsito donde se encuentren matriculados los vehículos.
              </p>
              <p>
                <strong>No asumimos ningún tipo de responsabilidad</strong> en cualquier inexactitud presentada en el
                informe final entregado al cliente, dado que la veracidad y actualización de los datos depende exclusivamente
                de las entidades oficiales que administran dichas bases de datos.
              </p>
            </Section>

            <Section title="2. Compromiso por Consulta de la Información">
              <p>
                Nuestra plataforma ejecutará la consulta por el tipo y número de documento indicado por el cliente en el
                pedido. Dicha información entregada podrá ser confrontada por los clientes directamente en la oficina de
                instrumentos públicos y organismo de tránsito respectivo.
              </p>
              <p>
                El cliente es responsable de suministrar correctamente el tipo y número de documento de la persona a
                consultar. rastreadordebienes.co no se hace responsable por consultas realizadas con datos incorrectos
                proporcionados por el usuario.
              </p>
            </Section>

            <Section title="3. Política de Devolución de Dinero">
              <p>
                <strong>No aplicará la devolución de dinero</strong> cuando la persona consultada no tenga ninguna
                propiedad o vehículo a su nombre registrado en las bases de datos oficiales.
              </p>
              <p><strong>Solo aplicará la devolución de dinero cuando:</strong></p>
              <ol>
                <li>No sea posible realizar la consulta de la información por causas imputables a la plataforma.</li>
                <li>Hayan transcurrido más de <strong>15 días calendario</strong> sin que se haya enviado la información al correo electrónico registrado por el cliente.</li>
                <li>Cuando el cliente aplique el <strong>derecho de retracto</strong> dentro de los primeros <strong>5 días hábiles</strong> de realizado el pedido. No aplicará el derecho de retracto si ya se ha realizado el envío de la información al cliente.</li>
              </ol>
            </Section>

            <Section title="4. Alcance del Informe">
              <p>Nuestro informe entregará información relacionada <strong>exclusivamente</strong> a:</p>
              <ul>
                <li><strong>PROPIEDADES:</strong> Matrículas, ciudad de ubicación y direcciones de las propiedades registradas a nombre de la persona consultada.</li>
                <li><strong>VEHÍCULOS:</strong> Placas de los vehículos registrados a nombre de la persona consultada.</li>
              </ul>

              <p><strong>El servicio NO incluye:</strong></p>
              <ul>
                <li>Certificados de tradición y libertad de propiedades.</li>
                <li>Certificado de tradición de vehículo expedido por el organismo de tránsito.</li>
                <li>Certificado de historial vehicular.</li>
              </ul>

              <p>
                Nuestra plataforma <strong>no puede determinar coeficientes de propiedad</strong>, es decir, no se puede
                establecer si la persona tiene un 100% o un porcentaje menor sobre el bien que se identifica como de su
                propiedad. Tampoco es posible determinar si existen otros propietarios o copropietarios sobre los bienes
                y vehículos consultados.
              </p>
            </Section>

            <Section title="5. Uso Legítimo del Servicio">
              <p>
                Al utilizar esta plataforma, el cliente declara que la consulta se realiza con fines legítimos y acordes
                con la normativa colombiana vigente, incluyendo la Ley 1581 de 2012 de Protección de Datos Personales y
                la Ley 1266 de 2008 sobre Habeas Data. Queda prohibido el uso de la información obtenida para fines
                ilícitos, discriminatorios o contrarios a la dignidad humana.
              </p>
            </Section>

            <Section title="6. Responsables del Tratamiento de Datos">
              <p>
                <strong>SYSTELEC ELECTRONICA E INFORMATICA</strong><br />
                Carrera 8 #18-24 Of. 611, Ciudad de Pereira, Risaralda, Colombia.
              </p>
              <p>
                Para consultas, solicitudes de eliminación, corrección o acceso a sus datos personales, puede
                contactarnos a través de los canales dispuestos en nuestra plataforma.
              </p>
            </Section>

            <Section title="7. Modificaciones a los Términos">
              <p>
                rastreadordebienes.co se reserva el derecho de modificar estos términos y condiciones en cualquier momento.
                Los cambios serán publicados en esta página con la fecha de actualización correspondiente. El uso continuado
                del servicio posterior a cualquier modificación constituye la aceptación de los nuevos términos.
              </p>
            </Section>

          </div>
        </div>

        <div className="mt-6 text-center">
          <button onClick={onBack}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-white text-sm"
            style={{ backgroundColor: '#2563EB' }}>
            <ArrowLeft size={16} />
            Volver a la consulta
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-bold mb-3 pb-2 border-b"
        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        {children}
      </div>
    </div>
  );
}
